'use client';

import React, { useState, useEffect } from 'react';
import { Navbar, Footer } from '../../components/Layout';
import { ShieldCheck, Lock, Eye, BookOpen, Clock, FileText } from 'lucide-react';

export default function PrivacyPolicyPage() {
  const [privacyPolicyContent, setPrivacyPolicyContent] = useState<string>('');

  useEffect(() => {
    const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';
    fetch(`${authApi}/api/settings`)
      .then(res => res.json())
      .then(settings => {
        if (settings.privacy_policy_content) {
          setPrivacyPolicyContent(settings.privacy_policy_content);
        }
      })
      .catch(err => console.error('Error fetching privacy policy settings:', err));
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F9F5] font-body text-text-primary flex flex-col justify-between">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 w-full flex-grow text-left">
        {/* Page Header */}
        <div className="mb-10 border-b border-border/60 pb-6">
          <h1 className="text-4xl font-display font-black text-deep-green">Privacy Policy</h1>
          <p className="text-text-muted text-sm font-medium mt-2">
            Learn how we collect, store, protect, and use your personal information when you use Pasumai Virumbi.
          </p>
        </div>

        <div className="space-y-8">
          {/* Quick Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-border/40 shadow-sm flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-pale-green/30 rounded-full flex items-center justify-center text-mid-green mb-4">
                <Lock className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-deep-green text-base mb-1">Data Security</h3>
              <p className="text-xs text-text-muted">We use industry-standard encryption protocols to protect your personal details.</p>
            </div>
            
            <div className="bg-white p-6 rounded-2xl border border-border/40 shadow-sm flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-pale-green/30 rounded-full flex items-center justify-center text-mid-green mb-4">
                <Eye className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-deep-green text-base mb-1">Zero Spam Policy</h3>
              <p className="text-xs text-text-muted">We never sell or share your mobile number or email with third-party advertisers.</p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-border/40 shadow-sm flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-pale-green/30 rounded-full flex items-center justify-center text-mid-green mb-4">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-deep-green text-base mb-1">GDPR & NPOP Compliant</h3>
              <p className="text-xs text-text-muted">You have full control over your data. Request deletion at any time from your profile.</p>
            </div>
          </div>

          {/* Policy Sections */}
          <div className="bg-white p-6 sm:p-8 rounded-3xl border border-border/40 shadow-sm space-y-6">
            {privacyPolicyContent ? (
              <div className="text-sm text-text-muted leading-relaxed whitespace-pre-line">
                {privacyPolicyContent}
              </div>
            ) : (
              <>
                <section className="space-y-3">
                  <h2 className="text-xl font-bold text-deep-green flex items-center gap-2">
                    <FileText className="w-5 h-5 text-mid-green" /> 1. Information We Collect
                  </h2>
                  <p className="text-sm text-text-muted leading-relaxed">
                    We collect personal information that you provide to us directly during account registration and checkout. This includes your username, email address, verified mobile phone number, coordinate-pinned shipping addresses, and purchase history.
                  </p>
                </section>

                <section className="space-y-3">
                  <h2 className="text-xl font-bold text-deep-green flex items-center gap-2">
                    <Lock className="w-5 h-5 text-mid-green" /> 2. How We Protect Your Data
                  </h2>
                  <p className="text-sm text-text-muted leading-relaxed">
                    Your account passwords and credentials are encrypted using secure hashing algorithms. All network communications are transmitted using secure HTTPS protocols. Map coordinate coordinates are securely linked to your orders only to assist delivery riders in routing.
                  </p>
                </section>

                <section className="space-y-3">
                  <h2 className="text-xl font-bold text-deep-green flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-mid-green" /> 3. Verified Communications
                  </h2>
                  <p className="text-sm text-text-muted leading-relaxed">
                    To maintain checkout security, prevent fraudulent entries, and enable automated order routing, we leverage mobile number OTP verification. We only send transactional notices (such as order status updates or dispatch alerts) to your registered mobile number.
                  </p>
                </section>

                <section className="space-y-3">
                  <h2 className="text-xl font-bold text-deep-green flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-mid-green" /> 4. Your Rights
                  </h2>
                  <p className="text-sm text-text-muted leading-relaxed">
                    You have the right to access, modify, or delete your account records at any time. For requests relating to complete data erasure or questions regarding our processing policies, please reach out to our privacy compliance team.
                  </p>
                </section>
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
